import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, CheckCircle2, Banknote } from "lucide-react";
import { toast } from "sonner";

const localeMap = { fr: "fr-FR", th: "th-TH", en: "en-US" };
const cellStyle = {
  paid: "bg-[#EAF4F0] text-[#2D6A4F] border-[#cfe6db]",
  due: "bg-gold-light text-terracotta border-[#f0dcc4]",
  overdue: "bg-[#F9EAEB] text-destructive border-[#f0d2d3]",
};

export default function Dues() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const canPay = user?.role !== "reception";
  const qc = useQueryClient();
  const { data: dues = [], isLoading } = useQuery({ queryKey: ["dues"], queryFn: () => api.get("/dues").then((r) => r.data) });

  const monthLabel = (period) => {
    const [y, m] = period.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(localeMap[lang] || "fr-FR", { month: "short", year: "2-digit" });
  };

  const markPaid = async (row) => {
    const next = row.months.find((m) => m.status !== "paid");
    if (!next) return;
    if (!window.confirm(t("dues.confirmPaid"))) return;
    const [y, m] = next.period.split("-");
    await api.post("/payments", {
      reservation_id: row.reservation_id, tenant_id: row.tenant_id, unit_id: row.unit_id,
      kind: "rent", amount: row.rent_amount, method: "cash", status: "paid",
      period_label: next.period, note: `${t("dues.rent")} ${next.period}`, date: `${y}-${m}-01T00:00:00+00:00`,
    });
    qc.invalidateQueries({ queryKey: ["dues"] });
    qc.invalidateQueries({ queryKey: ["payments"] });
    qc.invalidateQueries({ queryKey: ["stats"] });
    toast.success(t("dues.markPaid"));
  };

  const totalOverdue = dues.reduce((a, r) => a + r.overdue, 0);
  const totalDue = dues.reduce((a, r) => a + r.due_now, 0);
  const totalOutstanding = dues.reduce((a, r) => a + r.outstanding, 0);

  const cards = [
    { label: t("dues.overdue"), value: totalOverdue, icon: AlertTriangle, cls: "text-destructive" },
    { label: t("dues.dueNow"), value: totalDue, icon: Clock, cls: "text-terracotta" },
    { label: t("dues.outstanding"), value: money(totalOutstanding), icon: Banknote, cls: "text-teak" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-light tracking-tight text-teak">{t("dues.title")}</h1>
        <p className="text-muted-foreground mt-1">{t("dues.subtitle")}</p>
      </div>

      <div className="grid grid-cols-3 gap-4 max-w-2xl">
        {cards.map((c) => (
          <Card key={c.label} className="p-4 border-border" data-testid={`dues-stat-${c.label}`}>
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.08em] text-muted-foreground">{c.label}</span><c.icon className={`w-4 h-4 ${c.cls}`} /></div>
            <div className="text-2xl font-semibold mt-2">{c.value}</div>
          </Card>
        ))}
      </div>

      {isLoading ? null : dues.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground border-dashed">{t("dues.empty")}</Card>
      ) : (
        <div className="space-y-3">
          {dues.map((row) => {
            const clear = row.overdue === 0 && row.due_now === 0;
            return (
              <Card key={row.reservation_id} className="p-4 border-border" data-testid={`dues-row-${row.reservation_id}`}>
                <div className="flex flex-wrap items-center gap-4">
                  <div className="w-40 shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-semibold">{row.unit_number}</span>
                      {clear ? <CheckCircle2 className="w-4 h-4 text-[#2D6A4F]" /> :
                        row.overdue > 0 ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#F9EAEB] text-destructive">{row.overdue} {t("dues.overdue")}</span> :
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gold-light text-terracotta">{t("dues.dueNow")}</span>}
                    </div>
                    <div className="text-sm text-muted-foreground truncate">{row.tenant_name}</div>
                    <div className="text-xs text-muted-foreground">{money(row.rent_amount)}/{t("res.monthly").toLowerCase()}</div>
                  </div>

                  <div className="flex-1 flex flex-wrap gap-1.5 min-w-[240px]">
                    {row.months.slice(-14).map((m) => (
                      <div key={m.period} title={m.period}
                        className={`px-2 py-1 rounded-md border text-[11px] font-medium ${cellStyle[m.status]}`}
                        data-testid={`due-cell-${row.reservation_id}-${m.period}`}>
                        {monthLabel(m.period)}
                      </div>
                    ))}
                  </div>

                  {!clear && canPay && (
                    <Button size="sm" onClick={() => markPaid(row)} className="bg-teak hover:bg-teak-hover text-white shrink-0" data-testid={`mark-paid-${row.reservation_id}`}>
                      <Banknote className="w-4 h-4 mr-1" />{t("dues.markPaid")}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
